/**
 * Store Pinia d'authentification pour la plateforme Anamnesis
 * Gère l'état global d'authentification, la session et la persistence
 */

import { apiClient } from '@/services/api';
import { authService } from '@/services/authService';
import type {
    AdminResetPasswordRequest,
    AuthError,
    ChangePasswordRequest,
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    RegisterResponse,
    TokenConfig,
    User
} from '@/types/auth';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// Configuration du token
const TOKEN_CONFIG: TokenConfig = {
  storageKey: 'anamnesis_auth_token',
  expiryBuffer: 5, // Minutes avant expiration pour avertir
  maxAge: 30, // Durée de vie en minutes (correspond au backend)
};

/**
 * Store d'authentification utilisant la Composition API de Pinia
 */
export const useAuthStore = defineStore('auth', () => {
  // ===============================
  // État (state)
  // ===============================
  
  const user = ref<User | null>(null);
  const token = ref<string | null>(null);
  
  // États de chargement
  const isLoading = ref(false);
  const isLoggingIn = ref(false);
  const isRegistering = ref(false);
  const isChangingPassword = ref(false);
  const isFetchingUser = ref(false);
  
  // Cache pour éviter les requêtes multiples
  const lastUserFetch = ref<number>(0);
  const userFetchCache = 30000; // 30 secondes
  const pendingUserFetch = ref<Promise<User> | null>(null);
  
  // Gestion d'erreurs
  const error = ref<string | null>(null);
  
  // Session
  const lastActivity = ref<Date | null>(null);
  const tokenExpiry = ref<Date | null>(null);

  // ===============================
  // Getters (computed)
  // ===============================
  
  const isAuthenticated = computed((): boolean => {
    return !!(token.value && user.value && !isTokenExpired.value);
  });

  const isAdmin = computed((): boolean => {
    return user.value?.role === 'admin' || user.value?.role === 'superadmin' || false;
  });

  const isSuperAdmin = computed((): boolean => {
    return user.value?.role === 'superadmin' || false;
  });

  const userName = computed((): string | null => {
    return user.value?.name || null;
  });

  const userEmail = computed((): string | null => {
    return user.value?.email || null;
  });

  const hasValidToken = computed((): boolean => {
    return !!(token.value && !isTokenExpired.value);
  });

  const isTokenExpired = computed((): boolean => {
    if (!token.value) return true;
    return authService.isTokenExpired(token.value);
  });

  const timeUntilExpiry = computed((): number | null => {
    if (!token.value) return null;
    return authService.getTimeUntilExpiry(token.value);
  });

  const shouldWarnExpiry = computed((): boolean => {
    const timeRemaining = timeUntilExpiry.value;
    if (!timeRemaining) return false;
    
    const warningThreshold = TOKEN_CONFIG.expiryBuffer * 60 * 1000; // Convertir en ms
    return timeRemaining <= warningThreshold && timeRemaining > 0;
  });

  // ===============================
  // Helpers de Permissions
  // ===============================

  /**
   * Vérifie si l'utilisateur peut éditer un asset
   */
  const canEditAsset = computed((): ((asset: { user_id: string }) => boolean) => {
    return (asset: { user_id: string }): boolean => {
      if (!user.value) return false;
      return user.value.role === 'admin' || user.value.role === 'superadmin' || asset.user_id === user.value.uuid;
    };
  });

  /**
   * Vérifie si l'utilisateur peut supprimer un asset
   */
  const canDeleteAsset = computed((): ((asset: { user_id: string }) => boolean) => {
    return (asset: { user_id: string }): boolean => {
      if (!user.value) return false;
      return user.value.role === 'admin' || user.value.role === 'superadmin' || asset.user_id === user.value.uuid;
    };
  });

  /**
   * Vérifie si l'utilisateur peut voir un asset
   */
  const canViewAsset = computed((): ((asset: { user_id: string; is_public: boolean }) => boolean) => {
    return (asset: { user_id: string; is_public: boolean }): boolean => {
      if (!user.value) return asset.is_public;
      return user.value.role === 'admin' || user.value.role === 'superadmin' || asset.user_id === user.value.uuid || asset.is_public;
    };
  });

  /**
   * Vérifie si l'utilisateur est propriétaire d'un asset
   */
  const isAssetOwner = computed((): ((asset: { user_id: string }) => boolean) => {
    return (asset: { user_id: string }): boolean => {
      if (!user.value) return false;
      return asset.user_id === user.value.uuid;
    };
  });

  // ===============================
  // Actions
  // ===============================

  /**
   * Initialise le store depuis le localStorage au démarrage
   */
  const initializeFromStorage = async (): Promise<void> => {
    const storedToken = localStorage.getItem(TOKEN_CONFIG.storageKey);
    
    if (storedToken) {
      // Vérifier si le token n'est pas expiré
      if (!authService.isTokenExpired(storedToken)) {
        token.value = storedToken;
        updateTokenExpiry();
        
        // Récupérer les infos utilisateur de manière synchrone
        try {
          await fetchCurrentUser();
          console.log('État d\'authentification restauré depuis le localStorage');
        } catch (error) {
          // Si échec, nettoyer le token invalide
          console.warn('Token invalide détecté lors de l\'initialisation, nettoyage...', error);
          logout();
        }
      } else {
        // Token expiré, le supprimer
        console.log('Token expiré détecté lors de l\'initialisation, suppression...');
        localStorage.removeItem(TOKEN_CONFIG.storageKey);
      }
    }
  };

  /**
   * Met à jour l'heure d'expiration du token
   */
  const updateTokenExpiry = (): void => {
    if (!token.value) {
      tokenExpiry.value = null;
      return;
    }

    const payload = authService.parseJwtToken(token.value);
    if (payload?.exp) {
      tokenExpiry.value = new Date(payload.exp * 1000);
    }
  };

  /**
   * Met à jour la dernière activité
   */
  const updateLastActivity = (): void => {
    lastActivity.value = new Date();
  };

  /**
   * Configure l'intercepteur d'authentification pour l'API client
   */
  const setupApiInterceptors = (): void => {
    apiClient.setInterceptors({
      onRequest: async (options) => {
        // Ajouter automatiquement le token d'auth si disponible
        if (token.value && hasValidToken.value) {
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${token.value}`,
          };
        }
        
        updateLastActivity();
        return options;
      },
      onError: async (error) => {
        // Déconnexion automatique si token expiré
        if (error.status === 401 && isAuthenticated.value) {
          console.warn('Token expiré détecté, déconnexion automatique');
          await logout();
        }
        
        throw error;
      },
    });
  };

  /**
   * Sauvegarde le token dans le localStorage
   */
  const saveToken = (authToken: string): void => {
    token.value = authToken;
    localStorage.setItem(TOKEN_CONFIG.storageKey, authToken);
    updateTokenExpiry();
    setupApiInterceptors();
  };

  /**
   * Supprime le token du localStorage
   */
  const removeToken = (): void => {
    token.value = null;
    tokenExpiry.value = null;
    localStorage.removeItem(TOKEN_CONFIG.storageKey);
  };

  /**
   * Connexion utilisateur
   */
  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    isLoggingIn.value = true;
    error.value = null;

    try {
      const response = await authService.login(credentials);
      
      // Sauvegarder le token
      saveToken(response.access_token);
      
      // Si le backend retourne les infos utilisateur, les utiliser
      if (response.user) {
        user.value = response.user;
        console.log(`Connexion réussie pour ${response.user.email}`);
      } else {
        // Sinon, récupérer les infos utilisateur avec le token
        console.log('Connexion réussie, récupération des informations utilisateur...');
        try {
          const userData = await authService.getCurrentUser(response.access_token);
          user.value = userData;
          console.log(`Informations utilisateur récupérées pour ${userData.email}`);
        } catch (userError) {
          console.error('Erreur lors de la récupération des informations utilisateur:', userError);
          // Ne pas faire échouer le login si on ne peut pas récupérer les infos user
          // L'utilisateur sera redirigé et les infos seront récupérées plus tard
        }
      }
      
      return response;
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de connexion:', authError);
      throw authError;
      
    } finally {
      isLoggingIn.value = false;
    }
  };

  /**
   * Inscription utilisateur
   */
  const register = async (userData: RegisterRequest): Promise<RegisterResponse> => {
    isRegistering.value = true;
    error.value = null;

    try {
      const response = await authService.register(userData);
      
      console.log(`Inscription réussie pour ${userData.email}`, {
        emailSent: response.email_sent,
        userUuid: response.user_uuid,
      });
      
      return response;
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur d\'inscription:', authError);
      throw authError;
      
    } finally {
      isRegistering.value = false;
    }
  };

  /**
   * Récupération du profil utilisateur actuel avec cache
   */
  const fetchCurrentUser = async (forceRefresh = false): Promise<User> => {
    if (!token.value) {
      throw new Error('Aucun token d\'authentification');
    }

    // Vérifier le cache si pas de force refresh
    const now = Date.now();
    if (!forceRefresh && user.value && lastUserFetch.value > 0 && (now - lastUserFetch.value) < userFetchCache) {
      return user.value;
    }

    // Si une requête est déjà en cours, attendre son résultat
    if (pendingUserFetch.value) {
      return pendingUserFetch.value;
    }

    isFetchingUser.value = true;
    error.value = null;

    // Créer la promesse de requête
    pendingUserFetch.value = (async (): Promise<User> => {
      try {
        const userData = await authService.getCurrentUser(token.value!);
        user.value = userData;
        lastUserFetch.value = now;
        
        return userData;
        
      } catch (err) {
        const authError = err as AuthError;
        error.value = authError.message;
        
        console.error('Erreur de récupération utilisateur:', authError);
        
        // Si token invalide, déconnecter
        if (authError.code === 'TOKEN_EXPIRED') {
          await logout();
        }
        
        throw authError;
        
      } finally {
        isFetchingUser.value = false;
        pendingUserFetch.value = null;
      }
    })();

    return pendingUserFetch.value;
  };

  /**
   * Changement de mot de passe
   */
  const changePassword = async (passwords: ChangePasswordRequest): Promise<void> => {
    if (!token.value) {
      throw new Error('Non authentifié');
    }

    isChangingPassword.value = true;
    error.value = null;

    try {
      await authService.changePassword(passwords, token.value);
      
      console.log('Mot de passe changé avec succès');
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de changement de mot de passe:', authError);
      throw authError;
      
    } finally {
      isChangingPassword.value = false;
    }
  };

  /**
   * Réinitialisation de mot de passe par admin (pour les admins uniquement)
   */
  const resetUserPassword = async (data: AdminResetPasswordRequest): Promise<void> => {
    if (!token.value) {
      throw new Error('Non authentifié');
    }

    if (!isAdmin.value) {
      throw new Error('Permissions insuffisantes');
    }

    isLoading.value = true;
    error.value = null;

    try {
      await authService.adminResetPassword(data, token.value);
      
      console.log(`Mot de passe réinitialisé pour l'utilisateur ${data.user_uuid}`);
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de réinitialisation admin:', authError);
      throw authError;
      
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Demande de réinitialisation de mot de passe (mot de passe oublié)
   */
  const requestPasswordReset = async (email: string): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      // Note: Cette méthode n'existe pas encore dans authService
      // Elle sera implémentée quand le backend aura l'endpoint correspondant
      console.log(`Demande de réinitialisation de mot de passe pour ${email}`);
      
      // Pour l'instant, on simule une réponse réussie
      // await authService.requestPasswordReset(email);
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de demande de réinitialisation:', authError);
      throw authError;
      
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Déconnexion
   */
  const logout = async (): Promise<void> => {
    console.log('Déconnexion de l\'utilisateur');
    
    // Nettoyer l'état
    user.value = null;
    removeToken();
    lastActivity.value = null;
    error.value = null;
    
    // Nettoyer le cache
    lastUserFetch.value = 0;
    pendingUserFetch.value = null;
    
    // Réinitialiser les états de chargement
    isLoading.value = false;
    isLoggingIn.value = false;
    isRegistering.value = false;
    isChangingPassword.value = false;
    isFetchingUser.value = false;
    
    // Vider tous les caches des stores
    try {
      // Vider le cache des assets
      const { useAssetsCacheStore } = await import('@/stores/assetsCache');
      const assetsCacheStore = useAssetsCacheStore();
      assetsCacheStore.invalidateAllCache();
      assetsCacheStore.invalidateTagsCache();
      console.log('✅ Cache des assets vidé');
      
      // Vider le cache des schémas
      const { useSchemasStore } = await import('@/stores/schemasStore');
      const schemasStore = useSchemasStore();
      schemasStore.clearCache();
      console.log('✅ Cache des schémas vidé');
      
      // Vider le cache des traductions
      const { useTranslations } = await import('@/composables/useTranslations');
      const { clearCache: clearTranslationsCache } = useTranslations();
      clearTranslationsCache();
      console.log('✅ Cache des traductions vidé');
      
      // Vider le cache des traductions unifiées
      const { useUnifiedTranslations } = await import('@/composables/useUnifiedTranslations');
      const { clearAllCaches: clearUnifiedCache } = useUnifiedTranslations();
      clearUnifiedCache();
      console.log('✅ Cache des traductions unifiées vidé');
      
      // Vider le cache des mappings d'export
      const { useExportMappingsStore } = await import('@/stores/exportMappings');
      const exportMappingsStore = useExportMappingsStore();
      exportMappingsStore.clearCaches();
      console.log('✅ Cache des mappings d\'export vidé');
      
      // Vider le cache des mappings
      const { useMappings } = await import('@/composables/map/useMappings');
      const { clearCache: clearMappingsCache } = useMappings();
      clearMappingsCache();
      console.log('✅ Cache des mappings vidé');
      
      // Vider le cache de performance
      const { clearMemoCache } = await import('@/utils/performanceUtils');
      clearMemoCache();
      console.log('✅ Cache de performance vidé');
      
      // Vider le cache des collections
      const { useCollectionsStore } = await import('@/stores/collections');
      const collectionsStore = useCollectionsStore();
      collectionsStore.$reset();
      console.log('✅ Cache des collections vidé');
      
      // Vider le cache des sections
      const { useSectionsStore } = await import('@/stores/sectionsStore');
      const sectionsStore = useSectionsStore();
      sectionsStore.$reset();
      console.log('✅ Cache des sections vidé');
      
      // Vider le cache des breadcrumbs
      const { useBreadcrumbsStore } = await import('@/stores/breadcrumbs');
      const breadcrumbsStore = useBreadcrumbsStore();
      breadcrumbsStore.resetBreadcrumbs();
      console.log('✅ Cache des breadcrumbs vidé');
      
    } catch (error) {
      console.warn('⚠️ Erreur lors du vidage des caches:', error);
    }
  };

  /**
   * Vérification périodique de l'expiration du token
   */
  const checkTokenExpiry = async (): Promise<boolean> => {
    if (!token.value) return false;
    
    if (isTokenExpired.value) {
      console.warn('Token expiré détecté lors de la vérification');
      await logout();
      return false;
    }
    
    return true;
  };

  /**
   * Rafraîchissement des données utilisateur
   */
  const refreshUserData = async (): Promise<void> => {
    if (!hasValidToken.value) return;
    
    try {
      await fetchCurrentUser();
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données utilisateur:', error);
    }
  };

  /**
   * Efface l'erreur actuelle
   */
  const clearError = (): void => {
    error.value = null;
  };

  // ===============================
  // Initialisation
  // ===============================
  
  // Configuration des intercepteurs API au chargement du store
  setupApiInterceptors();

  // ===============================
  // Interface du store
  // ===============================
  
  return {
    // État
    user,
    token,
    isLoading,
    isLoggingIn,
    isRegistering,
    isChangingPassword,
    isFetchingUser,
    error,
    lastActivity,
    tokenExpiry,
    
    // Getters
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    userName,
    userEmail,
    hasValidToken,
    isTokenExpired,
    timeUntilExpiry,
    shouldWarnExpiry,
    
    // Helpers de Permissions
    canEditAsset,
    canDeleteAsset,
    canViewAsset,
    isAssetOwner,
    
    // Actions
    initializeFromStorage,
    updateLastActivity,
    setupApiInterceptors,
    login,
    register,
    fetchCurrentUser,
    changePassword,
    resetUserPassword,
    requestPasswordReset,
    logout,
    checkTokenExpiry,
    refreshUserData,
    clearError,
  };
});

// Export des types pour utilisation externe
export type AuthStore = ReturnType<typeof useAuthStore>; 